import React, { useState, useEffect, useRef, useMemo } from 'react';
import { gsap } from 'gsap';
import './Offer.scss';

import { useGetOnboardingMasterData } from 'src/api/mutations/useGetOnboardingMasterData';
import { useSaveOnboardingStep } from 'src/api/mutations/useSaveOnboardingStep';
import { sessionStore } from 'src/store/sessionStore';
import { getUserIdFromToken } from 'src/utils/authUtils';
import toast from 'src/utils/appAlert';
import { useGetOnboardingState } from 'src/api/mutations/useGetOnboardingState';

interface OfferProps {
  onNext?: (selectedOffers: string[]) => void;
  onBack?: () => void;
}

export const Offer: React.FC<OfferProps> = ({ onNext, onBack }) => {
  const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
  const [customOffers, setCustomOffers] = useState<string[]>([]);
  const [customInputValue, setCustomInputValue] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const pillsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const { data: masterData } = useGetOnboardingMasterData();
  const { mutate: saveStep, isPending } = useSaveOnboardingStep();

  const token = sessionStore((s) => s.token);
  const userId = getUserIdFromToken(token);

  const offerData = masterData?.['onboarding_offer'];
  const PREDEFINED_OFFERS = useMemo(() => offerData?.options || [], [offerData?.options]);

  // Render order: Predefined -> Custom -> "Other"
  const allOffersToRender = [
    ...PREDEFINED_OFFERS,
    ...customOffers.map((title) => ({ slug: title, title, isCustom: true })),
    { slug: 'other', title: 'Other' },
  ];

  const { data: stateData } = useGetOnboardingState(userId || undefined);

  useEffect(() => {
    if (stateData?.onboarding?.step3_offers && selectedOffers.length === 0) {
      const savedOffers = stateData.onboarding.step3_offers;
      setSelectedOffers(savedOffers);

      // Identify custom offers
      if (PREDEFINED_OFFERS.length > 0) {
        const predefinedSlugs = PREDEFINED_OFFERS.map((o) => o.slug);
        const customs = savedOffers.filter(
          (o: string) => !predefinedSlugs.includes(o) && o !== 'other',
        );
        setCustomOffers(customs);
      }
    }
  }, [stateData?.onboarding.step3_offers, PREDEFINED_OFFERS, selectedOffers.length]);

  useEffect(() => {
    // Only animate pills that haven't been animated yet (they still have inline opacity: 0)
    const newPills = pillsRef.current.filter((el) => el && el.style.opacity === '0');

    if (newPills.length > 0) {
      gsap.fromTo(
        newPills,
        { opacity: 0, scale: 0.8, y: 15 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.05,
          ease: 'back.out(1.5)',
        },
      );
    }
  }, [allOffersToRender.length]);

  const handleToggleOffer = (offer: string) => {
    setSelectedOffers((prev) =>
      prev.includes(offer) ? prev.filter((o) => o !== offer) : [...prev, offer],
    );
  };

  const handleAddCustomOffer = () => {
    const rawTokens = customInputValue
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!rawTokens.length) return;

    const invalidTokens = rawTokens.filter((token) => !/\p{L}/u.test(token));
    if (invalidTokens.length > 0) {
      setErrorMsg('Offers must contain at least one valid letter.');
      return;
    }

    const newCustomOffers = [...customOffers];
    const newSelectedOffers = [...selectedOffers];

    rawTokens.forEach((trimmed) => {
      const existingPredefined = PREDEFINED_OFFERS.find(
        (o) => o.title.toLowerCase() === trimmed.toLowerCase(),
      );

      if (existingPredefined) {
        if (!newSelectedOffers.includes(existingPredefined.slug)) {
          newSelectedOffers.push(existingPredefined.slug);
        }
      } else {
        if (!newCustomOffers.includes(trimmed)) {
          newCustomOffers.push(trimmed);
        }
        if (!newSelectedOffers.includes(trimmed)) {
          newSelectedOffers.push(trimmed);
        }
      }
    });

    setCustomOffers(newCustomOffers);
    setSelectedOffers(newSelectedOffers);
    setCustomInputValue('');
  };

  const handleNext = () => {
    if (selectedOffers.length === 0) return;

    const isOtherSelectedLocal = selectedOffers.includes('other');

    if (isOtherSelectedLocal && customOffers.length === 0 && !customInputValue.trim()) {
      setErrorMsg('Please specify what else you offer.');
      return;
    } else {
      setErrorMsg('');
    }

    const finalSelectedOffers = [...selectedOffers];
    const newCustomOffers = [...customOffers];

    // Auto-add if they typed something but forgot to click Add
    if (isOtherSelectedLocal && customInputValue.trim()) {
      const rawTokens = customInputValue
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const invalidTokens = rawTokens.filter((token) => !/\p{L}/u.test(token));
      if (invalidTokens.length > 0) {
        setErrorMsg('Offers must contain at least one valid letter.');
        return;
      }

      rawTokens.forEach((trimmed) => {
        const existingPredefined = PREDEFINED_OFFERS.find(
          (o) => o.title.toLowerCase() === trimmed.toLowerCase(),
        );

        if (existingPredefined) {
          if (!finalSelectedOffers.includes(existingPredefined.slug)) {
            finalSelectedOffers.push(existingPredefined.slug);
          }
        } else {
          if (!newCustomOffers.includes(trimmed)) {
            newCustomOffers.push(trimmed);
          }
          if (!finalSelectedOffers.includes(trimmed)) {
            finalSelectedOffers.push(trimmed);
          }
        }
      });
    }

    // Don't save 'other' to backend
    const payloadData = finalSelectedOffers.filter((o) => o !== 'other');

    if (payloadData.length === 0) {
      toast.error('Please select at least one valid offer.');
      return;
    }

    if (userId) {
      saveStep({ uid: userId, step: 3, data: payloadData });
    }
    if (onNext) onNext(payloadData);
  };

  const isOtherSelected = selectedOffers.includes('other');

  return (
    <div className="offer-container">
      <div className="offer-header">
        <h1 className="title">{offerData?.title || 'What do you offer?'}</h1>
        <p className="subtitle">{offerData?.subtitle || 'Select as many as you like'}</p>
      </div>

      <div className="offer-pills-container">
        {allOffersToRender.map((offer, index) => {
          const isSelected = selectedOffers.includes(offer.slug);
          const isCustom = (offer as any).isCustom;
          return (
            <button
              key={offer.slug}
              ref={(el) => {
                pillsRef.current[index] = el;
              }}
              className={`offer-pill ${isSelected ? 'selected' : ''} ${isCustom ? 'custom-pill' : ''}`}
              onClick={() => handleToggleOffer(offer.slug)}
              style={{ opacity: 0 }}
            >
              {offer.title}
              {isCustom && (
                <span
                  className="remove-custom"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomOffers((prev) => prev.filter((o) => o !== offer.slug));
                    setSelectedOffers((prev) => prev.filter((o) => o !== offer.slug));
                  }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 3L3 9M3 3L9 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isOtherSelected && (
        <div className="custom-input-section">
          <label className="custom-input-label">Please specify what else you offer:</label>
          <div className="custom-input-wrapper">
            <input
              type="text"
              placeholder="E.g., Yoga, Pilates..."
              value={customInputValue}
              onChange={(e) => {
                setCustomInputValue(e.target.value);
                if (errorMsg) setErrorMsg('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddCustomOffer();
              }}
            />
            <button
              className="btn-add"
              onClick={handleAddCustomOffer}
              disabled={!customInputValue.trim()}
            >
              Add
            </button>
          </div>
          {errorMsg && (
            <p
              className="error-text"
              style={{ color: '#E44E4E', fontSize: '14px', marginTop: '8px' }}
            >
              {errorMsg}
            </p>
          )}
        </div>
      )}

      <div className="offer-footer">
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>
        <div className="footer-actions">
          <button
            className="btn-back"
            onClick={onBack || (() => window.history.back())}
            disabled={isPending}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>

          <div className="next-btn-wrapper">
            <button
              className={`btn-next ${selectedOffers.length > 0 ? 'active' : ''}`}
              onClick={handleNext}
              disabled={selectedOffers.length === 0}
            >
              Next
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
